import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Textarea,
} from '@zuruflex/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DeclineBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: { reference: string; guestName: string; vehicle: string; when: string };
  isPending: boolean;
  /**
   * A refusal that came back from the API. It is shown in here rather than
   * on the page, which is behind the overlay and unreadable while this is
   * open — a failed decline would otherwise look like nothing happening.
   */
  error?: string | null;
  onConfirm: (reason: string | undefined) => void;
}

/**
 * Turning a reservation down.
 *
 * This replaced a `window.prompt`. Three things were wrong with the prompt
 * and all three mattered: it did not say which booking was about to be
 * refused, it did not say that the sentence typed into it goes to the
 * customer unedited, and an accidental Enter turned the hire down with an
 * empty reason. A dialog can name the hire, say who reads the reason, and
 * keep the destructive word on a button you have to aim at.
 */
export function DeclineBookingDialog({
  open,
  onOpenChange,
  booking,
  isPending,
  error,
  onConfirm,
}: DeclineBookingDialogProps) {
  const { t } = useTranslation(['partner', 'common']);
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('bookings.declineTitle')}</DialogTitle>
          <DialogDescription>
            {t('bookings.declineBody', {
              guest: booking.guestName,
              vehicle: booking.vehicle,
              date: booking.when,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <FormField label={t('bookings.declineReasonLabel')} hint={t('bookings.declineReason')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={3}
                maxLength={500}
                autoFocus
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder={t('bookings.declineReasonPlaceholder')}
              />
            )}
          </FormField>

          <Alert variant="warning">{t('bookings.declineNotice')}</Alert>

          {error ? <Alert variant="danger">{error}</Alert> : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common:action.cancel')}
          </Button>
          <Button
            variant="destructive"
            isLoading={isPending}
            onClick={() => onConfirm(reason.trim() || undefined)}
          >
            {t('bookings.declineConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
